import { queryOptions, useQuery } from '@tanstack/react-query';
import { generateListResponseScheme, type ListRespondedWith } from '@/models/response';
import { type Friend, friendScheme } from '@/models/user/friend';
import { fetcher } from '@/utils/fetcher';

type OrderedFriendMap = Record<
  number,
  Friend & {
    Order: number;
  }
>;

export async function getFriends(signal?: AbortSignal) {
  const response = await fetcher<ListRespondedWith<Friend>>('/GetFriends', undefined, {
    signal,
    schema: generateListResponseScheme(friendScheme),
    validResponseCodes: [0, 1],
  });

  if (!response?.Data) return {};

  return response.Data.reduce<OrderedFriendMap>((accu, friend, index) => {
    accu[friend.FriendID] = { ...friend, Order: index };

    return accu;
  }, {});
}

export const friendsQueryOptions = queryOptions({
  queryKey: ['/GetFriends'],
  queryFn: ({ signal }) => getFriends(signal),
  refetchInterval: 15 * 60 * 1000,
});

export default function useFriends() {
  const query = useQuery(friendsQueryOptions);

  return {
    friends: query.data ?? {},
    friendCount: Object.keys(query.data ?? {}).length,
    ...query,
  };
}

export function useFriend(userId: Nullable<number>) {
  const query = useQuery({
    ...friendsQueryOptions,
    select: (data) => (data && userId ? data[userId] : undefined),
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    friend: query.data,
    isBefriended: Boolean(query.data),
    ...query,
  };
}

export function useFriendBySessionId(sessionId: Nullable<number>) {
  const query = useQuery({
    ...friendsQueryOptions,
    select: (data) =>
      data && sessionId ? Object.values(data).find((friend) => friend.ChatSessionID === sessionId) : undefined,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    friend: query.data,
    isBefriended: Boolean(query.data),
    ...query,
  };
}
